//src/pages/gro/SurveyCampaignsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { BarChart3, Plus, Search, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  usePublishSurveyCampaign,
  useSurveyCampaigns,
} from "@/hooks/useSurveyCampaigns";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { SurveyCampaignStatus } from "@/types/gro";

function statusLabel(status: SurveyCampaignStatus) {
  if (status === "draft") return "Rascunho";
  if (status === "published") return "Publicada";
  if (status === "closed") return "Encerrada";
  return "Cancelada";
}

function modeLabel(mode?: string | null) {
  if (mode === "identified") return "Identificada";
  if (mode === "anonymous") return "Anônima";
  if (mode === "pseudonymized") return "Pseudoanônima";
  return "-";
}

function formatCampaignDateTime(value?: string | null) {
  if (!value) return "-";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(d);

  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${map.day}/${map.month}/${map.year} ${map.hour}:${map.minute}`;
}

function formatProgress(value?: number | null) {
  if (typeof value !== "number" || Number.isNaN(value)) return "0,00%";
  return `${value.toFixed(2).replace(".", ",")}%`;
}

export default function SurveyCampaignsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canRead =
    user?.permissions?.includes(PERMISSIONS.SURVEY_CAMPAIGNS_READ) ?? false;
  const canWrite =
    user?.permissions?.includes(PERMISSIONS.SURVEY_CAMPAIGNS_WRITE) ?? false;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SurveyCampaignStatus | "all">("all");
  const [publishTargetId, setPublishTargetId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(
    () => ({
      q: debouncedSearch,
      status: statusFilter === "all" ? "" : statusFilter,
    }),
    [debouncedSearch, statusFilter],
  );

  const listQ = useSurveyCampaigns(params);
  const publishMut = usePublishSurveyCampaign();

  const items = listQ.data ?? [];

  async function handlePublish() {
    if (!publishTargetId) return;

    try {
      const result = await publishMut.mutateAsync(publishTargetId);
      toast.success(
        `Campanha publicada com sucesso. Tokens gerados: ${result.tokens_created}.`,
      );
      setPublishTargetId(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao publicar campanha.");
    }
  }

  return (
    <div className="pb-10">
      <PageHeader
        title="Campanhas"
        description="Crie, publique e acompanhe campanhas psicossociais."
      >
        {canWrite ? (
          <Button onClick={() => navigate("/gro/campaigns/novo")}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Campanha
          </Button>
        ) : null}
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as SurveyCampaignStatus | "all")
            }
          >
            <SelectTrigger className="lg:w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="published">Publicada</SelectItem>
              <SelectItem value="closed">Encerrada</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">
          Você não possui permissão para visualizar campanhas.
        </Card>
      ) : listQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando campanhas...
        </Card>
      ) : listQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(listQ.error as any)?.message || "Erro ao carregar campanhas"}
          </div>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhuma campanha encontrada"
          description="Crie uma campanha e publique para disponibilizar respostas."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campanha</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Modo</TableHead>
                <TableHead className="text-center">Participantes</TableHead>
                <TableHead className="text-center">Responderam</TableHead>
                <TableHead className="text-center">% Avanço</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Término</TableHead>
                <TableHead className="w-[180px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className="font-medium">{item.name}</span>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={
                        item.status === "published"
                          ? "default"
                          : item.status === "draft"
                          ? "secondary"
                          : "outline"
                      }
                    >
                      {statusLabel(item.status)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline">{modeLabel(item.anonymous_mode)}</Badge>
                  </TableCell>

                  <TableCell className="text-center">
                    {item.participants_count}
                  </TableCell>

                  <TableCell className="text-center">
                    {item.responded_count}
                  </TableCell>

                  <TableCell className="text-center">
                    {formatProgress(item.progress_percentage)}
                  </TableCell>

                  <TableCell>{formatCampaignDateTime(item.starts_at)}</TableCell>
                  <TableCell>{formatCampaignDateTime(item.ends_at)}</TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {item.responded_count > 0 ? (
                        <PermissionIconButton
                          label="Ver resultados"
                          permission={PERMISSIONS.SURVEY_RESULTS_READ}
                          onClick={() => navigate(`/gro/campaigns/${item.id}/resultados`)}
                        >
                          <BarChart3 className="h-4 w-4" />
                        </PermissionIconButton>
                      ) : null}
                      
                      {canWrite && item.status === "draft" ? (
                        <PermissionIconButton
                          label="Publicar"
                          permission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                          disabled={publishMut.isPending}
                          onClick={() => setPublishTargetId(item.id)}
                        >
                          <Upload className="h-4 w-4" />
                        </PermissionIconButton>
                      ) : null}

                      <CrudRowActions
                        onView={() => navigate(`/gro/campaigns/${item.id}`)}
                        onEdit={
                          canWrite && item.status === "draft"
                            ? () => navigate(`/gro/campaigns/${item.id}/editar`)
                            : undefined
                        }
                        viewPermission={PERMISSIONS.SURVEY_CAMPAIGNS_READ}
                        editPermission={PERMISSIONS.SURVEY_CAMPAIGNS_WRITE}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={!!publishTargetId}
        onOpenChange={(open) => {
          if (!open) setPublishTargetId(null);
        }}
        title="Publicar Campanha"
        description="Deseja publicar esta campanha agora? Após a publicação, a pesquisa ficará disponível para os participantes elegíveis."
        onConfirm={handlePublish}
        confirmLabel="Publicar"
      />
    </div>
  );
}
